(() => {
  if (customElements.get('cart-drawer')) return;
  const root = window.Shopify?.routes?.root || '/';
  const parse = html => new DOMParser().parseFromString(html, 'text/html');
  const focusable = el => [...el.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex="0"]')].filter(node => node.getClientRects().length && getComputedStyle(node).visibility !== 'hidden');
  let returnFocus;
  let changing = false;

  class SoultieCartDrawer extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.addEventListener('click', event => {
        if (event.target.closest('#CartDrawer-Overlay')) this.close();
        const remove = event.target.closest('cart-remove-button');
        if (remove) {
          event.preventDefault();
          this.changeQuantity(Number(remove.dataset.index), 0);
          return;
        }
        const button = event.target.closest('quantity-input button[name]');
        if (!button || button.closest('quantity-input').matches(':defined')) return;
        const input = button.closest('quantity-input').querySelector('input');
        if (!input || changing) return;
        event.preventDefault();
        const step = Number(input.step) || 1;
        const next = Number(input.value) + (button.name === 'plus' ? step : -step);
        if (next < 0 || (input.max && next > Number(input.max))) return;
        this.changeQuantity(Number(input.dataset.index), next);
      }, options);
      this.addEventListener('change', event => {
        const input = event.target.closest('input[name="updates[]"]');
        if (input) this.changeQuantity(Number(input.dataset.index), Number(input.value));
      }, options);
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
        }
        if (event.key !== 'Tab' || !this.classList.contains('active')) return;
        const nodes = focusable(this.querySelector('.drawer__inner'));
        if (!nodes.length) { event.preventDefault(); return; }
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (event.shiftKey && (document.activeElement === first || !nodes.includes(document.activeElement))) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault(); first.focus();
        }
      }, options);
    }

    disconnectedCallback() { this.abort?.abort(); }
    setActiveElement(element) { if (element) returnFocus = element; }
    getSectionsToRender() {
      return [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }];
    }

    open(element) {
      if (element) this.setActiveElement(element);
      else if (!this.contains(document.activeElement)) this.setActiveElement(document.activeElement);
      this.classList.add('animate', 'active');
      this.setAttribute('open', '');
      document.body.classList.add('overflow-hidden');
      document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => {
        const inner = this.querySelector('.drawer__inner');
        if (inner) (focusable(inner)[0] || inner).focus({ preventScroll: true });
      });
    }

    close() {
      this.classList.remove('active');
      this.removeAttribute('open');
      document.body.classList.remove('overflow-hidden');
      document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'false');
      if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
    }

    renderContents(state, keepClosed = false) {
      const html = state?.sections?.['cart-drawer'];
      if (!html) throw new Error(window.cartStrings?.error || 'Cart unavailable');
      const next = parse(html).querySelector('cart-drawer');
      if (!next) throw new Error(window.cartStrings?.error || 'Cart unavailable');
      this.classList.toggle('is-empty', next.classList.contains('is-empty'));
      this.replaceChildren(...next.childNodes);
      const bubbleHtml = state.sections['cart-icon-bubble'];
      const bubble = document.querySelector('#cart-icon-bubble');
      if (bubble && bubbleHtml) {
        const parsed = parse(bubbleHtml);
        const nextBubble = parsed.querySelector('#cart-icon-bubble');
        bubble.innerHTML = nextBubble ? nextBubble.innerHTML : (parsed.querySelector('.shopify-section') || parsed.body).innerHTML;
      }
      if (!keepClosed) this.open();
    }

    async refresh() {
      const url = new URL(window.location.href);
      url.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error(window.cartStrings?.error || 'Cart unavailable');
      this.renderContents({ sections: await response.json() });
    }

    async changeQuantity(line, quantity) {
      if (changing || !Number.isInteger(line) || line < 1 || !Number.isInteger(quantity) || quantity < 0) return;
      changing = true;
      const row = this.querySelector(`[data-index="${line}"].cart-item`);
      const priorQuantity = row?.dataset.quantity;
      const controls = [...this.querySelectorAll('button,input,select')].filter(el => !el.disabled);
      controls.forEach(el => { el.disabled = true; });
      this.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(`${root}cart/change.js`, {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ line, quantity, sections: ['cart-drawer', 'cart-icon-bubble'], sections_url: window.location.pathname })
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || window.cartStrings?.error);
        this.renderContents(data);
      } catch (error) {
        const message = this.querySelector('#CartDrawer-CartErrors');
        if (message) { message.textContent = error.message || window.cartStrings?.error; message.setAttribute('role', 'alert'); }
        if (priorQuantity !== undefined) {
          const input = row?.querySelector('input[name="updates[]"]');
          if (input) input.value = priorQuantity;
        }
      } finally {
        changing = false;
        controls.forEach(el => { if (el.isConnected) el.disabled = false; });
        this.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('cart-drawer', SoultieCartDrawer);

  if (!customElements.get('product-form')) {
    class SoultieProductForm extends HTMLElement {
      connectedCallback() {
        this.abort = new AbortController();
        this.addEventListener('submit', event => this.handleSubmit(event), { signal: this.abort.signal });
      }
      disconnectedCallback() { this.abort?.abort(); }

      async handleSubmit(event) {
        if (event.defaultPrevented) return;
        const form = this.querySelector('form');
        if (!form) return;
        const skipCart = event.currentTarget?.dataset.skipCartButton || form.querySelector('[data-skip-cart="true"]');
        event.preventDefault();
        // The existing sticky button calls this method. Delegate its click to
        // the primary button so bundle apps retain ownership of their offers.
        if (event.type === 'click' && !event.currentTarget?.dataset.skipCartButton) {
          event.stopImmediatePropagation();
          const sticky = event.target.closest('sticky-atc');
          const selected = sticky?.querySelector('select option:checked');
          const picker = document.getElementById(`variant-selects-${this.dataset.section}`);
          if (selected && picker && !picker.matches(':defined')) {
            const variants = JSON.parse(picker.querySelector('script[type="application/json"]').textContent);
            const variant = variants.find(item => String(item.id) === selected.dataset.variantId);
            if (variant) {
              [...picker.querySelectorAll('.product-form__input')].forEach((group, index) => {
                group.querySelectorAll('input[type="radio"]').forEach(input => { input.checked = input.value === variant.options[index]; });
                const select = group.querySelector('select');
                if (select) select.value = variant.options[index];
              });
            }
          }
          form.querySelector('[type="submit"][name="add"]')?.click();
          return;
        }
        if (this.busy) return;
        if (!form.reportValidity()) return;
        const drawer = document.querySelector('cart-drawer');
        const errorBox = this.querySelector('.product-form__error-message-wrapper');
        const errorText = this.querySelector('.product-form__error-message');
        if (errorBox) errorBox.hidden = true;
        const buttons = [...form.querySelectorAll('button')].filter(button => !button.disabled);
        let added = false;
        this.busy = true;
        try {
          const data = new FormData(form);
          // main.js also owned the variant picker. Resolve the checked option
          // from the rendered product data before submitting a standard form.
          const picker = document.getElementById(`variant-selects-${this.dataset.section}`);
          if (picker && !picker.matches(':defined')) {
            const variants = JSON.parse(picker.querySelector('script[type="application/json"]').textContent);
            const options = [...picker.querySelectorAll('.product-form__input')].map(group => group.querySelector('input:checked,select')?.value);
            const variant = variants.find(item => item.options.every((option, index) => option === options[index]));
            if (!variant?.available) throw new Error(window.variantStrings?.unavailable || 'Ten wariant jest niedostępny.');
            data.set('id', variant.id);
          }
          data.set('sections', 'cart-drawer,cart-icon-bubble');
          data.set('sections_url', window.location.pathname);
          buttons.forEach(button => { button.disabled = true; });
          this.setAttribute('aria-busy', 'true');
          drawer?.setActiveElement(event.submitter || form.querySelector('[name="add"]'));
          const response = await fetch(`${root}cart/add.js`, {
            method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json' }, body: data
          });
          const result = await response.json();
          if (!response.ok || result.status) throw new Error(result.description || result.message || 'Nie udało się dodać produktu.');
          added = true;
          if (skipCart) {
            window.location.assign(`${root}checkout`);
          } else if (drawer) {
            drawer.renderContents(result);
          } else {
            window.location.assign(`${root}cart`);
          }
        } catch (error) {
          // Never repeat a successful add when only section rendering fails.
          if (added) window.location.assign(`${root}cart`);
          else if (errorBox && errorText) {
            errorText.textContent = error.message || 'Nie udało się dodać produktu. Sprawdź połączenie i spróbuj ponownie.';
            errorBox.hidden = false;
          }
        } finally {
          this.busy = false;
          buttons.forEach(button => { button.disabled = false; });
          this.removeAttribute('aria-busy');
        }
      }
    }
    customElements.define('product-form', SoultieProductForm);
  }
  document.addEventListener('click', event => {
    const link = event.target.closest('#cart-icon-bubble');
    if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    event.preventDefault();
    drawer.open(link);
  });
})();